import { useEffect, useState } from "react";

/** Tag prices are set in rupees; dollars are always derived from them. */
export const TAG_PRICES_INR = { qr: 5, nfc: 200 } as const;

/** Used until (or if) today's rate can't be fetched. */
export const FALLBACK_INR_PER_USD = 98;

const CACHE_KEY = "hkp-site-usd-inr";
const RATE_URL = "https://open.er-api.com/v6/latest/USD";

export type UsdRate = { inrPerUsd: number; asOf?: string; live: boolean };

type Cached = { day: string; inrPerUsd: number; asOf?: string };

const today = () => new Date().toISOString().slice(0, 10);

const readCache = (): Cached | null => {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    return cached && cached.day === today() && cached.inrPerUsd > 0 ? cached : null;
  } catch {
    return null;
  }
};

/** USD→INR rate, fetched at most once a day per browser; falls back to ₹98. */
export const useUsdRate = (): UsdRate => {
  const cached = readCache();
  const [rate, setRate] = useState<UsdRate>(
    cached
      ? { inrPerUsd: cached.inrPerUsd, asOf: cached.asOf, live: true }
      : { inrPerUsd: FALLBACK_INR_PER_USD, live: false }
  );

  useEffect(() => {
    if (readCache()) return;
    let alive = true;
    fetch(RATE_URL)
      .then((res) => res.json())
      .then((data) => {
        const inrPerUsd = Number(data?.rates?.INR);
        if (data?.result !== "success" || !(inrPerUsd > 0)) return;
        const asOf: string | undefined = data.time_last_update_utc;
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ day: today(), inrPerUsd, asOf }));
        } catch {
          // private mode — the rate still applies for this visit
        }
        if (alive) setRate({ inrPerUsd, asOf, live: true });
      })
      .catch(() => {
        // offline or blocked — keep the fallback rate
      });
    return () => {
      alive = false;
    };
  }, []);

  return rate;
};

/** $0.05 under ten dollars, whole dollars above. */
export const formatUsd = (inr: number, inrPerUsd: number): string => {
  const usd = inr / inrPerUsd;
  return usd < 10 ? `$${usd.toFixed(2)}` : `$${Math.round(usd).toLocaleString("en-US")}`;
};

export const formatInr = (inr: number): string => `₹${inr.toLocaleString("en-IN")}`;

/** "23 Oct 2026" from the API's RFC-1123 timestamp. */
export const formatAsOf = (asOf?: string): string => (asOf ? asOf.slice(5, 16) : "");
